import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { businessApi, staffApi } from '../services/api';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';
const DAYS = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];

interface Turn {
  startTime: string;
  endTime: string;
}

interface DayAvailability {
  dayOfWeek: number;
  isAvailable: boolean;
  turns: Turn[];
}

const DEFAULT: DayAvailability[] = [
  { dayOfWeek: 0, isAvailable: false, turns: [{ startTime: '09:00', endTime: '18:00' }] },
  { dayOfWeek: 1, isAvailable: true,  turns: [{ startTime: '09:00', endTime: '18:00' }] },
  { dayOfWeek: 2, isAvailable: true,  turns: [{ startTime: '09:00', endTime: '18:00' }] },
  { dayOfWeek: 3, isAvailable: true,  turns: [{ startTime: '09:00', endTime: '18:00' }] },
  { dayOfWeek: 4, isAvailable: true,  turns: [{ startTime: '09:00', endTime: '18:00' }] },
  { dayOfWeek: 5, isAvailable: true,  turns: [{ startTime: '09:00', endTime: '18:00' }] },
  { dayOfWeek: 6, isAvailable: false, turns: [{ startTime: '09:00', endTime: '14:00' }] },
];

function calcHours(start: string, end: string) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff <= 0) return '';
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return h > 0 ? `${h}h${m > 0 ? ` ${m}min` : ''}` : `${m}min`;
}

export function Availability() {
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [availability, setAvailability] = useState<DayAvailability[]>(DEFAULT.map(d => ({ ...d, turns: d.turns.map(t => ({ ...t })) })));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    businessApi.getMyBusinesses().then(r => {
      const list = r.data.data.businesses || [];
      setBusinesses(list);
      if (list.length > 0) { setSelectedBusiness(list[0].id); loadStaff(list[0].id); }
    }).finally(() => setIsLoading(false));
  }, []);

  const loadStaff = async (businessId: string) => {
    try {
      const r = await staffApi.getBusinessStaff(businessId);
      const list = r.data.data.staff || [];
      setStaff(list);
      if (list.length > 0) { setSelectedStaff(list[0].id); loadAvailability(list[0].id); }
      else setStaff([]);
    } catch {}
  };

  const loadAvailability = async (staffId: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      const r = await axios.get(`${API_URL}/availability/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: any[] = r.data.data.availability || [];
      if (data.length > 0) {
        const merged = DEFAULT.map(def => {
          const rows = data.filter(a => a.dayOfWeek === def.dayOfWeek);
          if (rows.length > 0) {
            return {
              dayOfWeek: def.dayOfWeek,
              isAvailable: rows.some(r => r.isAvailable),
              turns: rows.map(r => ({ startTime: r.startTime, endTime: r.endTime })),
            };
          }
          return { ...def, turns: def.turns.map(t => ({ ...t })) };
        });
        setAvailability(merged);
      } else {
        setAvailability(DEFAULT.map(d => ({ ...d, turns: d.turns.map(t => ({ ...t })) })));
      }
    } catch {}
  };

  const handleStaffChange = (id: string) => { setSelectedStaff(id); loadAvailability(id); };
  const handleBusinessChange = (id: string) => { setSelectedBusiness(id); loadStaff(id); };

  const toggleDay = (dayOfWeek: number, val: boolean) => {
    setAvailability(prev => prev.map(d => d.dayOfWeek === dayOfWeek ? { ...d, isAvailable: val } : d));
  };

  const updateTurn = (dayOfWeek: number, idx: number, field: 'startTime' | 'endTime', val: string) => {
    setAvailability(prev => prev.map(d => {
      if (d.dayOfWeek !== dayOfWeek) return d;
      const turns = d.turns.map((t, i) => i === idx ? { ...t, [field]: val } : t);
      return { ...d, turns };
    }));
  };

  const addTurn = (dayOfWeek: number) => {
    setAvailability(prev => prev.map(d => {
      if (d.dayOfWeek !== dayOfWeek) return d;
      return { ...d, turns: [...d.turns, { startTime: '16:00', endTime: '20:00' }] };
    }));
  };

  const removeTurn = (dayOfWeek: number, idx: number) => {
    setAvailability(prev => prev.map(d => {
      if (d.dayOfWeek !== dayOfWeek) return d;
      return { ...d, turns: d.turns.filter((_, i) => i !== idx) };
    }));
  };

  const handleSave = async () => {
    if (!selectedStaff) return;
    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      // Aplanar turns en filas individuales para el backend
      const flat = availability.flatMap(d =>
        d.turns.map(t => ({
          dayOfWeek: d.dayOfWeek,
          startTime: t.startTime,
          endTime: t.endTime,
          isAvailable: d.isAvailable,
        }))
      );
      await axios.post(`${API_URL}/availability/staff/${selectedStaff}`,
        { availability: flat },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Horarios guardados correctamente');
    } catch {
      toast.error('Error al guardar los horarios');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Horarios del personal</h1>
        <p className="text-secondary-500 text-sm mt-1">Define cuándo está disponible cada miembro del equipo. Puedes añadir varios turnos por día.</p>
      </div>

      <div className="card p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Negocio</label>
            <select className="input" value={selectedBusiness} onChange={e => handleBusinessChange(e.target.value)}>
              {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Empleado</label>
            <select className="input" value={selectedStaff} onChange={e => handleStaffChange(e.target.value)}>
              {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
            </select>
          </div>
        </div>
      </div>

      {staff.length > 0 ? (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-secondary-200 bg-secondary-50">
            <h2 className="font-semibold text-secondary-900">Horario semanal</h2>
          </div>
          <div className="divide-y divide-secondary-100">
            {availability.map(day => (
              <div key={day.dayOfWeek} className="px-6 py-4">
                <div className="flex items-start gap-4">
                  {/* Checkbox + nombre día */}
                  <div className="flex items-center gap-3 w-32 pt-1 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={day.isAvailable}
                      onChange={e => toggleDay(day.dayOfWeek, e.target.checked)}
                      className="h-4 w-4 rounded border-secondary-300 text-primary-600"
                    />
                    <span className={`font-medium text-sm ${day.isAvailable ? 'text-secondary-900' : 'text-secondary-400'}`}>
                      {DAYS[day.dayOfWeek]}
                    </span>
                  </div>

                  {/* Turnos */}
                  {day.isAvailable ? (
                    <div className="flex-1 space-y-2">
                      {day.turns.map((turn, idx) => (
                        <div key={idx} className="flex items-center gap-2 flex-wrap">
                          <input
                            type="time"
                            value={turn.startTime}
                            onChange={e => updateTurn(day.dayOfWeek, idx, 'startTime', e.target.value)}
                            className="input w-32"
                          />
                          <span className="text-secondary-400">—</span>
                          <input
                            type="time"
                            value={turn.endTime}
                            onChange={e => updateTurn(day.dayOfWeek, idx, 'endTime', e.target.value)}
                            className="input w-32"
                          />
                          <span className="text-xs text-secondary-400 w-12">
                            {calcHours(turn.startTime, turn.endTime)}
                          </span>
                          {day.turns.length > 1 && (
                            <button
                              onClick={() => removeTurn(day.dayOfWeek, idx)}
                              className="text-red-400 hover:text-red-600 text-xs px-2 py-1 rounded hover:bg-red-50 transition-colors"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={() => addTurn(day.dayOfWeek)}
                        className="text-primary-600 hover:text-primary-700 text-xs font-medium flex items-center gap-1 mt-1"
                      >
                        + Añadir turno
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm text-secondary-400 italic pt-1">No disponible</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="px-6 py-4 border-t border-secondary-200 bg-secondary-50 flex justify-end">
            <button onClick={handleSave} disabled={isSaving} className="btn-primary">
              {isSaving ? 'Guardando...' : 'Guardar horarios'}
            </button>
          </div>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <p className="text-4xl mb-4">👥</p>
          <p className="text-secondary-500">No tienes personal en este negocio.</p>
          <a href="/staff" className="btn-primary mt-4 inline-block">Añadir personal</a>
        </div>
      )}
    </div>
  );
}
